import { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Link } from '@mui/material';
import { LayoutStyles as bundleClasses } from './LayoutStyles';
import { DeleteIcon, FileIcon, GreenTickFill } from '../../images/svg/CustomizedSVG';
import LeadGenerationContext from '../../context/LeadGenerationContext';
import { DocChecklistStyles as classes } from '../CommercialDeposit/DragDropFiles/DocChecklistStyles';
import { productMapping } from '../../services/productMapping';
import { CLICK_TO_VIEW, ELITE_ACCOUNT_EN, LIBRE_AHORRA_BUNDLE } from '../../constants';
import { uploadFileStatement } from '../Sidebar/StatementUpload';

export default function LinearWithValueLabel(props) {
  // eslint-disable-next-line react/prop-types
  const { pagePath, history, onTriggerUpload } = props;
  const fileInput = useRef(null);
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const {
    statementUploadStatus,
    statementUploadLoading,
    statementFileName,
    statementFileSize,
    statementDocumentId,
    statementUploadProgress,
    setIsFundingScreenDirty,
    setIsEdeliveryScreenDirty,
    setStatementUploadStatus,
    setStatementFileName,
    setStatementFileSize,
    setStatementDocumentId,
    setFundData,
    setFundDataErrors,
    setStatementUploadProgress,
    statementLinearProgress,
    statementError,
    bankUploadPreviewUrl,
    theme,
    isLightTheme,
    setBankUploadPreviewUrl,
  } = leadGenerationContext;

  const checkFileInput = () => {
    if (fileInput?.current?.value) {
      fileInput.current.value = '';
    }
  };

  const buttonClickHandler = (e) => {
    e.preventDefault();
    checkFileInput();
    fileInput.current?.click();
  };

  const fileInputHandler = async (e) => {
    e.preventDefault();
    const file = e?.target?.files?.[0];
    if (file !== undefined && file !== null) {
      setBankUploadPreviewUrl(URL.createObjectURL(file));
      uploadFileStatement(file, leadGenerationContext, props);
    }
  };

  const removeUploadedFile = () => {
    setIsFundingScreenDirty(true);
    setIsEdeliveryScreenDirty(true);
    setStatementUploadStatus(null);
    setStatementFileName('');
    setStatementFileSize('');
    setStatementUploadProgress(0);
    setStatementDocumentId(null);

    if (
      productMapping(leadGenerationContext.productName) === ELITE_ACCOUNT_EN ||
      productMapping(leadGenerationContext.productName) === 'Ahorra' ||
      productMapping(leadGenerationContext.productName) === LIBRE_AHORRA_BUNDLE
    ) {
      setFundData((prevState) => ({
        ...prevState,
        accountNumber: '',
        typeOfAccount: '',
        bank: '',
        otherBank: '',
        transitNumber: '',
      }));
      setFundDataErrors((prevState) => ({
        ...prevState,
        accountNumber: '',
        typeOfAccount: '',
        bank: '',
        otherBank: '',
        transitNumber: '',
      }));
    }
  };
  return (
    <Box
      component="div"
      key={statementDocumentId}
      sx={classes.gridItem}
      role="section"
      width="100%"
    >
      <div>
        {/* main draggable container */}
        {!statementUploadLoading && (
          <Box
            component="div"
            aria-roledescription="bank statement"
            // className="drop-file-input"
            sx={{
              position: 'relative',
            }}
          >
            <Box component="span">
              {(statementUploadStatus === 'FAILED' || statementError === 'exceeded') &&
                !statementUploadLoading && (
                  <Grid container padding="10px">
                    <Grid size={{ md: 1 }} sx={bundleClasses.verificationPending}>
                      <FileIcon
                        color={
                          statementUploadStatus === 'FAILED'
                            ? '#D5151F'
                            : statementUploadStatus === 'DONE'
                            ? '#FFFFFF'
                            : theme?.palette?.subtext?.main
                        }
                      />
                    </Grid>
                    <Grid size={{ md: 10 }}>
                      <Typography component="p" color="danger" sx={bundleClasses.fileUploadName}>
                        {t('The document did not upload. Please try again.', {
                          defaultValue: 'The document did not upload. Please try again.',
                        })}
                      </Typography>
                      <Typography
                        component="h5"
                        color="danger"
                        paddingY="5px"
                        sx={bundleClasses.fileUploadName}
                      >
                        {statementFileName}
                      </Typography>

                      {!statementUploadStatus === 'FAILED' && (
                        <Typography component="p" sx={bundleClasses.fileUploadSize}>
                          {Math.floor(statementFileSize / 1024)} KB
                        </Typography>
                      )}

                      <Link
                        component="button"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 700,
                          textDecoration: 'none',
                        }}
                        onClick={(e) => buttonClickHandler(e)}
                        onKeyDown={(e) => buttonClickHandler(e)}
                        color="danger"
                        // ref={fileInput}
                      >
                        {t('TryAgain_Bundle', { defaultValue: 'TryAgain_Bundle' })}
                      </Link>
                      {statementUploadStatus !== 'DONE' && (
                        <input
                          type="file"
                          data-testid="inp_test"
                          accept=".pdf,.jpg,.jpeg,.jfif,.bmp,.bpm,.pgm,.ppm,.png,.gif,.gifv,.tiff,.heic,.heif,.hevc,.mpg,.mpeg"
                          onChange={(e) => fileInputHandler(e, leadGenerationContext, props)}
                          ref={fileInput}
                          style={{ display: 'none' }}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
            </Box>
          </Box>
        )}

        {(statementUploadStatus === 'DONE' ||
          statementUploadLoading ||
          statementLinearProgress) && (
          <div>
            <Grid container spacing={2} sx={bundleClasses.fileUploadContainer}>
              <Grid size={{ md: 1 }} sx={bundleClasses.verificationPending}>
                <FileIcon
                  color={
                    statementUploadStatus === 'FAILED'
                      ? '#D5151F'
                      : statementUploadStatus === 'DONE' && !isLightTheme
                      ? '#FFFFFF'
                      : theme?.palette?.subtext?.main
                  }
                />
              </Grid>
              <Grid size={{ md: 10 }}>
                <Typography component="h5" paddingY="5px" sx={bundleClasses.fileUploadName}>
                  {statementFileName}
                </Typography>
                {statementFileSize && (
                  <Typography component="p" sx={bundleClasses.fileUploadSize}>
                    {Math.floor(statementFileSize / 1024)} KB
                  </Typography>
                )}
                {!statementUploadLoading && bankUploadPreviewUrl && (
                  <Link
                    style={{ wordBreak: 'break-all', paddingRight: '5px' }}
                    sx={[
                      classes.uploadedFileName,
                      {
                        fontWeight: 700,
                        textDecoration: 'none !important',
                        color: theme?.palette?.orientalPrimary?.main,
                      },
                    ]}
                    href={bankUploadPreviewUrl}
                    target="_blank"
                  >
                    {t(CLICK_TO_VIEW, {
                      defaultValue: CLICK_TO_VIEW,
                    })}
                  </Link>
                )}
              </Grid>
              <Grid size={{ md: 1 }}>
                {statementUploadProgress === 100 && statementLinearProgress ? (
                  <GreenTickFill height="20px" width="20px" />
                ) : (
                  <IconButton
                    id="closeIconId"
                    name={statementFileName}
                    data-testid={`cancel_testid_${statementFileName}`}
                    onClick={removeUploadedFile}
                    onKeyDown={removeUploadedFile}
                    size="small"
                    color="inherit"
                  >
                    <DeleteIcon
                      color={
                        statementUploadStatus === 'FAILED'
                          ? '#D5151F'
                          : statementUploadStatus === 'DONE' && !isLightTheme
                          ? '#FFFFFF'
                          : theme?.palette?.subtext?.main
                      }
                    />
                  </IconButton>
                )}
              </Grid>
              {statementLinearProgress && (
                <Grid size={{ md: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={statementUploadProgress}
                        // sx={[classes.linearBar, classes.progressColor]}

                        sx={{
                          backgroundColor: '#F58220', // Track color
                          '& .MuiLinearProgress-bar': {
                            backgroundColor:
                              statementUploadProgress === 100 ? '#0B6C03' : '#F58220',
                          },
                          '& .MuiLinearProgress-root': {
                            height: '20px !important',
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {`${Math.round(statementUploadProgress)}%`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </div>
        )}
      </div>
    </Box>
  );
}

LinearProgress.propTypes = {
  value: PropTypes.number,
  onTriggerUpload: PropTypes.func,
};
